import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, X } from "lucide-react";
import { leadsApi, type LeadGeneratorOption } from "@/api/leads";
import { leadKeys } from "@/hooks/leads/leadKeys";
import { Button, Input } from "@/design-system/components/ui";

interface LeadGeneratorPickerProps {
  /** Selected generator id (null = not attributed). */
  value: string | null;
  /** Resolved display name for the current value, when known (edit/detail). */
  valueLabel?: string | null;
  /** Fired when the user picks a generator, or clears (null). The parent decides
   *  whether that sets form state (create) or calls the endpoint (existing lead). */
  onChange: (option: LeadGeneratorOption | null) => void;
  /** External save in flight (detail-page inline save). */
  busy?: boolean;
  disabled?: boolean;
}

/**
 * Lead Generation — the Ownership "Lead Generator" attribution picker. Searches
 * ACTIVE generators by name (the phone staff usually know the caller's generator
 * by name) and surfaces the selection to the parent. Controlled: the New Lead
 * form binds it to form state; the detail page wires it to setLeadGenerator.
 */
export function LeadGeneratorPicker({
  value,
  valueLabel,
  onChange,
  busy,
  disabled,
}: LeadGeneratorPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const options = useQuery({
    queryKey: leadKeys.generatorOptions(debounced),
    queryFn: () => leadsApi.leadGeneratorOptions(debounced || undefined),
    enabled: open,
  });

  // Prefer the resolved name; fall back to a short id so the readout is never blank.
  const currentLabel = valueLabel ?? (value ? `${value.slice(0, 8)}…` : null);

  if (!open) {
    return (
      <div className="flex items-center gap-space-2 flex-wrap">
        <div className="flex items-center gap-space-1 text-small text-onyx">
          <Sparkles size={14} strokeWidth={1.5} aria-hidden />
          <span className="text-caption uppercase tracking-wide text-gray-mid">Lead Generator:</span>
          <span>{currentLabel ?? "Not attributed"}</span>
        </div>
        {!disabled && (
          <>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setSearch(""); setOpen(true); }}
              disabled={busy}
            >
              {value ? "Change" : "Attribute"}
            </Button>
            {value && (
              <Button size="sm" variant="tertiary" onClick={() => onChange(null)} disabled={busy}>
                {busy ? "Saving…" : "Clear"}
              </Button>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-2">
      <div className="flex items-center gap-space-2">
        <Input
          value={search}
          autoFocus
          placeholder="Search active generators by name…"
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Button size="sm" variant="tertiary" onClick={() => setOpen(false)} disabled={busy}>
          <X size={14} strokeWidth={1.5} aria-hidden /> Cancel
        </Button>
      </div>
      <div className="border border-border-light rounded-md bg-white-pure max-h-56 overflow-y-auto">
        {options.isLoading && (
          <div className="p-space-2 text-caption text-gray-mid">Searching…</div>
        )}
        {!options.isLoading && options.data?.length === 0 && (
          <div className="p-space-2 text-caption text-gray-mid">No active generators match.</div>
        )}
        {options.data?.map((g) => (
          <button
            key={g.id}
            type="button"
            className="w-full text-left px-space-2 py-space-1 border-b border-border-light last:border-b-0 hover:bg-white-warm text-small text-onyx disabled:opacity-50"
            disabled={busy}
            onClick={() => { onChange(g); setOpen(false); }}
          >
            <span className="font-medium">{g.displayName}</span>
            {g.email && <span className="text-caption text-gray-mid"> · {g.email}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
